import React, { useEffect, useMemo, useState } from "react";
import { Collapse, GridLegacy, MenuItem, Paper } from "@mui/material";
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import { convertArrayToSelectOptions, convertObjectToSelectOptions } from "../../../templateHelpers";
import { backofficeLanguages, valuta } from "../../../variables/app";
import TranslationWrapper, { getTranslation } from "../../../components/TranslationWrapper/TranslationWrapper.tsx";
import FormSelectTC from "../../../components/Forms/FormSelectTC";
import FormTextTC from "../../../components/Forms/FormTextTC";
import axios from "axios";
import FormSwitchTC from "../../../components/Forms/FormSwitchTC";
import FormAsyncTC from "../../../components/Forms/FormAsyncTC";
import { useFormContext, useWatch } from "react-hook-form";
import { connect } from "react-redux";
import currencyData from "currency-codes/data";
import FormAutocompleteTC from "../../../components/Forms/FormAutocompleteTC";

const appTalenOpties = convertObjectToSelectOptions(backofficeLanguages);

function Algemeen(props) {
  const [data, setData] = useState({ resellers: null });
  const { setValue } = useFormContext();

  const currencyOptions = useMemo(() => {
    return currencyData.map((currency) => ({
      value: currency.code,
      label: getTranslation(`currency.${currency.code}`, `${currency.currency}`) + ` (${currency.code})`,
    }));
  }, []);

  useEffect(() => {
    axios
      .all([
        axios.get(
          "core/resellers?per_page=2500&fields=id as value,companyName as label&sort_field=companyName&sort_dir=asc"
        ),
      ])
      .then(
        axios.spread((resellersResponse) => {
          setData({ resellers: resellersResponse.data.data.data });
        })
      );
  }, []);

  const resellersOptions = data.resellers ? convertArrayToSelectOptions(data.resellers) : [];

  const totp_confirmed = useWatch({ name: "totp_confirmed" });
  const userId = useWatch({ name: "id" });
  const masterId = useWatch({ name: "master_id" });

  const profileIdFieldProps = useMemo(() => {
    if (props.user.id === userId) {
      return { disabled: true };
    } else {
      return {};
    }
  }, [userId, props.user.id]);

  useEffect(() => {
    if (!masterId) {
      setValue("is_master", false);
      setValue("has_access_to_masters_salesareas", false);
    }
  }, [masterId, setValue]);

  return (
    <Card>
      <CardBody style={{ padding: 16 }}>
        <GridLegacy container spacing={1}>
          {totp_confirmed != null ? (
            <FormSwitchTC
              name={"totp_confirmed"}
              tDefault={"Reset 2FA"}
              tKey={"gebruikers.form.algemeen.label.2fa.reset"}
            />
          ) : null}
          <FormSwitchTC
            name={"totp_forced"}
            tDefault={"User preference 2FA (Enables 2FA when not required by policies)"}
            tKey={"gebruikers.form.algemeen.label.2fa.strict"}
          />
          <FormSelectTC
            name={"lang"}
            options={appTalenOpties}
            tDefault={"Voorkeurstaal"}
            tKey={"gebruikers.form.algemeen.label.lang"}
          />
          <FormAutocompleteTC
            name={"valuta"}
            options={currencyOptions}
            tDefault={"Valuta"}
            tKey={"gebruikers.form.algemeen.label.valuta"}
          />
          <FormTextTC name={"username"} tDefault={"Username"} tKey={"gebruikers.form.algemeen.label.username"} />
          <FormAsyncTC
            async={{
              url: "core/profiles/search?fields=id as value,name as label,profile_type_id,profile_type.rank,profile_type.id&per_page=50",
              searchField: "name",
              sort: (a, b) => a.profile_type.rank - b.profile_type.rank,
            }}
            fieldProps={profileIdFieldProps}
            name={"profile_id"}
            rules={{ required: { value: true, message: getTranslation("This field is required!") } }}
            tDefault={"Profile"}
            tKey={"gebruikers.form.algemeen.label.profile_id"}
          />
          <Paper
            sx={{
              width: 1,
              px: masterId ? 1 : undefined,
              "&.MuiPaper-root": { boxShadow: !masterId ? "none" : undefined },
            }}
          >
            <FormAsyncTC
              async={{
                url: "core/gebruikers/search?fields=id as value, email as label&per_page=50",
                searchField: "email",
                extraFilters: props?.model?.email
                  ? [
                      { field: "is_master", value: 1, operator: "=" },
                      { field: "email", value: props?.model?.email, operator: "!=" },
                    ]
                  : [{ field: "is_master", value: 1, operator: "=" }],
              }}
              name={"master_id"}
              nullable={true}
              tDefault={"Group manager"}
              tKey={"gebruikers.form.algemeen.label.master_id"}
            />
            <Collapse in={!!masterId} sx={{ width: 1 }}>
              <FormSwitchTC
                name={"is_master"}
                tDefault={"User has access to same users as group manager"}
                tKey={"gebruikers.form.algemeen.label.is_master"}
              />
              <FormSwitchTC
                name={"has_access_to_masters_salesareas"}
                tDefault={"User also has access to salesareas of group manager"}
                tKey={"gebruikers.form.algemeen.label.has_access_to_masters_salesareas"}
              />
            </Collapse>
          </Paper>
          <FormSelectTC
            name={"resellers_id"}
            options={[
              <MenuItem key={"default"} value={""}>
                <TranslationWrapper tDefault={"Geen reseller"} tKey={"gebruikers.form.algemeen.optie.resellerNVT"} />
              </MenuItem>,
              ...resellersOptions,
            ]}
            tDefault={"Is onderdeel van"}
            tKey={"gebruikers.form.algemeen.label.reseller"}
          />
          <FormTextTC name={"Voornaam"} tDefault={"Voornaam"} tKey={"gebruikers.form.algemeen.label.Voornaam"} />
          <FormTextTC
            name={"Voorvoegsel"}
            tDefault={"Voorvoegsel"}
            tKey={"gebruikers.form.algemeen.label.Voorvoegsel"}
          />
          <FormTextTC name={"Achternaam"} tDefault={"Achternaam"} tKey={"gebruikers.form.algemeen.label.Achternaam"} />
          <FormTextTC
            name={"email"}
            rules={{
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: getTranslation("Invalid e-mail address!"),
              },
              required: {
                value: true,
                message: getTranslation("Invalid e-mail address!"),
              },
            }}
            tDefault={"E-mail"}
            tKey={"gebruikers.form.algemeen.label.email"}
            textFieldType={"email"}
          />
          <FormTextTC name={"Telefoon"} tDefault={"Telefoon"} tKey={"gebruikers.form.algemeen.label.Telefoon"} />
          <FormTextTC
            name={"Bedrijfsnaam"}
            tDefault={"Bedrijfsnaam"}
            tKey={"gebruikers.form.algemeen.label.Bedrijfsnaam"}
          />
          {userId > 0 ? (
            <FormAsyncTC
              async={{
                url: "core/gebruikers/search?fields=id as value, email as label&per_page=50",
                searchField: "email",
              }}
              key={"created_by"}
              name={"created_by"}
              nullable={true}
              tDefault={"Created By"}
              tKey={"gebruikers.form.algemeen.label.created_by"}
            />
          ) : null}
          <FormSwitchTC
            name={"allowApiAccess"}
            tDefault={"Allow API read access"}
            tKey={"gebruikers.form.algemeen.label.allowApiAccess"}
          />
          {/* <Collapse in={allowApiAccess} sx={{ width: 1 }}>
            <FormSwitchTC
              name={"apiWriteAccess"}
              tKey={"gebruikers.form.algemeen.label.apiWriteAccess"}
              tDefault={"Allow API write access"}
            />
          </Collapse>*/}
        </GridLegacy>
      </CardBody>
    </Card>
  );
}

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(Algemeen);
